import { XMarkIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "@mui/material";
import { When } from "react-if";
import {
  OrganisationFeatureFlags,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";

import { useContact } from "../../../../hooks/useContact";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { WrappedLink } from "../../../WrappedLink";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { useSetParentContactMutation } from "../../../../services/api/contactsApi/contacts";

function useParentChildUnlink() {
  const currentOrg = useGetSelectedOrganisation();
  const [setParentContact] = useSetParentContactMutation();
  const canUnlink = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.MANUAL_PARENT_CHILD) && !!accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_MANUAL_PARENT_CHILD);

  function unlink(contactId: number) {
    if (!currentOrg?.id) return;

    setParentContact({
      organisationId: currentOrg.id,
      contactIds: [contactId],
      parentContactId: null,
    });
  }

  return { canUnlink, unlink, accountingSystemName: currentOrg?.accountingSystemName };
}

/** Link to the parent contact, with an unlink button for manual parent/child orgs */
export function ContactParentLink() {
  const { data: contact } = useContact();
  const { canUnlink, unlink, accountingSystemName } = useParentChildUnlink();

  if (!contact) return null;

  return (
    <>
      <When condition={ contact?.parentContactId }>
        <div className={ `flex items-center space-x-1` }>
          <WrappedLink
            to={ `/contacts/${contact.parentContactId}` }
            className={ `hover:underline text-indigo-600 hover:text-indigo-800` }
          >
            { `${ contact?.parentContactName } ↰` }
          </WrappedLink>
          <When condition={ canUnlink }>
            <Tooltip title={ `Remove parent link` }>
              <button
                onClick={ () => unlink(contact.id) }
                className={ `text-gray-400 hover:text-red-500` }
              >
                <XMarkIcon className={ `h-4 w-4` } />
              </button>
            </Tooltip>
          </When>
        </div>
        <When condition={ contact.parentContactArchived }>
          <Paragraph variant={ `warning` }>
            { `The parent contact is archived, so statements for this contact are not being sent. Remove the parent link or restore the parent in ${accountingSystemName}.` }
          </Paragraph>
        </When>
      </When>
    </>
  );
}

/** Links to child contacts, with unlink buttons for manual parent/child orgs */
export function ContactChildLinks() {
  const { data: contact } = useContact();
  const { canUnlink, unlink } = useParentChildUnlink();

  if (!contact) return null;

  return (
    <>
      <When condition={ contact?.childContacts && contact?.childContacts.length > 0 }>
        { contact.childContacts.map(child => (
          <div
            key={ child.id }
            className={ `flex items-center space-x-1` }
          >
            <Paragraph variant={ `link` }>
              <WrappedLink to={ `/contacts/${child.id}` }>
                { `↳ ${ child.name }` }
              </WrappedLink>
            </Paragraph>
            <When condition={ canUnlink }>
              <Tooltip title={ `Remove child link` }>
                <button
                  onClick={ () => child.id && unlink(child.id) }
                  className={ `text-gray-400 hover:text-red-500` }
                >
                  <XMarkIcon className={ `h-4 w-4` } />
                </button>
              </Tooltip>
            </When>
          </div>
        )) }
      </When>
    </>
  );
}
